import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  FileText, User, Phone, Mail, MapPin, Globe, KeyRound, Calendar, Briefcase, AlertCircle,
  MessageSquare, Loader2, CheckCircle2, ArrowRight, Sparkles, Eye, EyeOff,
  FileCheck2, PhoneCall, ShieldCheck, ArrowDown,
} from 'lucide-react';
import {
  INDIAN_STATES, LANGUAGES, SOURCES_OF_INCOME, FINANCIAL_YEARS, SERVICE_CATALOG,
  defaultSpokenLanguageForUiLocale,
} from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { serviceTitle } from '../i18n/serviceCopy';
import {
  buildCampaignCapturePayload,
  getJourneyId,
  trackEvent,
  trackPageLifecycle,
} from '../lib/customerEventTracker';
import { appendLeadToGoogleSheets, isGoogleSheetsConfigured } from '../lib/googleSheetsLead';
import { goToPostSubmit } from '../lib/postSubmitGuest';
import { translateIncomeSource, translateItrGuideField } from '../lib/itrSourceI18n';
import FormLegalNotice from '../components/FormLegalNotice';
import { SOURCE_ITR_PRICING_GUIDE } from '../data/itrPricingGuide';
import ServiceContent from '../components/ServiceContent';
import { SERVICE_CONTENT } from '../data/serviceContent';

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const initialState = {
  client_name: '', mobile: '', language: '', state: '', remarks: '',
  pan_number: '', password: '', financial_year: ['2024-25'], source_of_income: [], email_id: '',
};

const orderYears = (picked) => FINANCIAL_YEARS.filter((y) => picked.includes(y));

const orderSources = (picked) => SOURCES_OF_INCOME.filter((s) => picked.includes(s));

// ----- Helpers -----
const validateForm = (form, t) => {
  const e = {};
  if (!form.client_name.trim() || form.client_name.length > 150) e.client_name = t('form.err_name', 'Enter a valid name (up to 150 chars)');
  if (!/^[0-9]{10}$/.test(form.mobile)) e.mobile = t('form.err_mobile', 'Enter a valid 10-digit mobile number');
  if (!form.language) e.language = t('form.err_language', 'Please select preferred language');
  if (!form.state) e.state = t('form.err_state', 'Please select your state');
  if (form.pan_number && !PAN_REGEX.test(form.pan_number.toUpperCase())) e.pan_number = t('form.err_pan', 'PAN must match AAAAA9999A');
  const fy = Array.isArray(form.financial_year) ? form.financial_year : [];
  if (fy.length === 0) e.financial_year = t('form.err_fin_year_multi', 'Select at least one financial year');
  if (form.email_id && !/^\S+@\S+\.\S+$/.test(form.email_id)) e.email_id = t('form.err_email', 'Enter valid email');
  const soi = Array.isArray(form.source_of_income) ? form.source_of_income : [];
  if (soi.length === 0) e.source_of_income = t('form.err_source_multi', 'Select at least one source of income');
  return e;
};

const buildPayload = (form, serviceCode) => {
  const financialYearsOrdered = orderYears(Array.isArray(form.financial_year) ? form.financial_year : []);
  const sourcesOrdered = orderSources(Array.isArray(form.source_of_income) ? form.source_of_income : []);
  return {
    client_name: form.client_name.trim(),
    mobile: form.mobile.trim(),
    language: form.language,
    state: form.state,
    priority: 'NORMAL',
    remarks: form.remarks.trim() || null,
    pan_number: form.pan_number ? form.pan_number.toUpperCase().trim() : null,
    password: form.password || null,
    financial_year: financialYearsOrdered.join(', '),
    financial_years: financialYearsOrdered,
    filed_status: 'NOT_FILED',
    referral_id: null,
    referral_entity: null,
    email_id: form.email_id.trim() || null,
    source_of_income: sourcesOrdered.join(', '),
    sources_of_income: sourcesOrdered,
    service_code: serviceCode || null,
    refund_amount: null,
    rm_id: null,
    op_id: null,
    tag: 'client_website',
  };
};

/** Dynamic labels so each income-tax service (query `service=`) shows matching copy */
const getIncomeTaxServiceCopy = (serviceCode, serviceTitle, t) => {
  const title = (serviceTitle || t('itf.default_service', 'Income tax service')).trim();
  const step4Fb = {
    ITR_FILING: 'Get your ITR filed quickly',
    ITR_NOTICE: 'Get your notice response moving quickly',
    ADVANCE_TAX: 'Get your advance tax planned quickly',
    CAPITAL_GAINS: 'Get your capital gains numbers ready quickly',
  };
  const step4 = t(`itf.step4.${serviceCode}`, step4Fb[serviceCode] || t('itf.deliver_generic', 'We will deliver {title} quickly', { title }));
  const promoCta = t('common.get_my_service_done', 'Get my {service} done', { service: title });
  const heroFb = {
    ITR_FILING: 'help you file your ITR quickly and correctly.',
    ITR_NOTICE: 'help you plan a clear response to your income-tax notice.',
    ADVANCE_TAX: 'help you work out advance tax and next payment steps.',
    CAPITAL_GAINS: 'help you compute capital gains and the right way to report them.',
  };
  const heroHelp = t(`itf.hero.${serviceCode}`, heroFb[serviceCode] || t('itf.hero_generic', 'help you with {title} end to end.', { title: title.toLowerCase() }));
  const benefitFb = {
    ITR_FILING: 'File Your ITR and Get Maximum Refund',
    ITR_NOTICE: 'Clear notice strategy and support from our team',
    ADVANCE_TAX: 'Accurate advance tax numbers so you pay the right amount on time',
    CAPITAL_GAINS: 'Clean capital-gains working and documentation support',
  };
  const benefitLine = t(`itf.benefit.${serviceCode}`, benefitFb[serviceCode] || t('itf.benefit_generic', 'Fast support for {title}', { title: title.toLowerCase() }));
  return { step4, promoCta, heroHelp, benefitLine };
};

// ----- Subcomponents -----
const FormSection = ({ title, icon: Icon, children, hint }) => (
  <div>
    <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
      <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center"><Icon className="w-4 h-4 text-[#14A098]" /></div>
      <h2 className="font-bold text-[#0f2540] text-lg">{title}</h2>
      {hint && <span className="text-xs text-gray-500 ml-auto">{hint}</span>}
    </div>
    {children}
  </div>
);

const FieldWrap = ({ label, icon: Icon, required, error, hint, htmlFor, children }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="text-sm font-medium text-[#0f2540] flex items-center gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#14A098]" />} {label}
      {required && <span className="text-rose-500">*</span>}
    </Label>
    {children}
    {hint && !error && <p className="text-xs text-gray-400">{hint}</p>}
    {error && <p id={`${htmlFor}-error`} className="text-xs text-rose-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
  </div>
);

/** Checkbox list for multi-valued fields (financial years, income sources). */
function MultiCheckboxGroup({
  groupId,
  options,
  selected,
  onToggle,
  error,
  getOptionLabel,
}) {
  const selectedSet = new Set(selected);
  return (
    <div
      id={groupId}
      role="group"
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${groupId}-error` : undefined}
      className={`rounded-md border bg-background p-3 space-y-2.5 max-h-52 overflow-y-auto ${error ? 'border-rose-500 ring-1 ring-rose-500/30' : 'border-input'}`}
    >
      {options.map((opt) => (
        <label
          key={opt}
          htmlFor={`${groupId}-${encodeURIComponent(opt)}`}
          className="flex items-start gap-2.5 cursor-pointer text-sm font-medium text-[#0f2540] leading-snug"
        >
          <Checkbox
            id={`${groupId}-${encodeURIComponent(opt)}`}
            className="mt-0.5"
            checked={selectedSet.has(opt)}
            onCheckedChange={(c) => onToggle(opt, c === true)}
          />
          <span>{getOptionLabel ? getOptionLabel(opt) : opt}</span>
        </label>
      ))}
    </div>
  );
}

const ITR_SIDEBAR_STYLES = [
  'rounded-xl bg-gradient-to-r from-[#14A098] to-[#0e7a74] border border-[#14A098] shadow-md p-3 text-white',
  'rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 border border-cyan-500 shadow-md p-3 text-white',
  'rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 border border-sky-500 shadow-md p-3 text-white',
  'rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 border border-emerald-500 shadow-md p-3 text-white',
  'rounded-xl bg-gradient-to-r from-[#0f2540] to-[#14A098] border border-[#14A098] shadow-md p-3 text-white',
];

const ITR_SIDEBAR_ICONS = [FileCheck2, PhoneCall, Briefcase, ShieldCheck, Sparkles];

const FILING_ITR_SIDEBODY_FALLBACKS = [
  'Fill in your information and submit to get started.',
  'An expert will call you within 2 hours to understand your needs.',
  "Share the required documents—we'll guide you on what's needed.",
  'Our experts prepare and file your Income Tax Return.',
  "Your ITR is filed quickly and you'll receive confirmation.",
];

const Sidebar = ({ t, step4Text, serviceCode, submitDetailsLabel }) => {
  const isItrFiling = serviceCode === 'ITR_FILING';

  const bodies = isItrFiling
    ? FILING_ITR_SIDEBODY_FALLBACKS.map((fb, i) => t(`itr.filing_how_step${i + 1}`, fb))
    : [
        t('itr.s1_text', 'When your details are complete, tap "{cta}"', { cta: submitDetailsLabel }),
        t('itr.s2_text', 'ITR expert callback from our team within 2 hours'),
        t('itr.s3_docs_text', 'Our team collects the documents needed for your ITR'),
        t('itr.s3_text', 'We will provide the ITR services'),
        step4Text,
      ];

  const labels = [
    t('itr.step1', 'Step 1'),
    t('itr.step2', 'Step 2'),
    isItrFiling ? t('itr.step3', 'Step 3') : t('itr.step3_docs', 'Step 3'),
    t('itr.step4', 'Step 4'),
    t('itr.step5', 'Step 5'),
  ];

  return (
    <div className="w-full min-w-0">
      <Card className="rotating-border-card p-5 rounded-2xl bg-gradient-to-br from-white via-teal-50/60 to-cyan-50/50 shadow-lg shadow-[#14A098]/20">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#14A098]/10 border border-[#14A098]/25 mb-4">
          <span className="w-2 h-2 rounded-full bg-[#14A098]" />
          <span className="text-xs font-semibold uppercase tracking-wider text-[#0f2540]">{t('itr.quick_process', 'Quick Process')}</span>
        </div>
        <div className="space-y-3 text-sm">
          {labels.map((label, idx) => {
            const Icon = ITR_SIDEBAR_ICONS[idx];
            return (
              <div key={`itr-sidebar-${idx}`} className={ITR_SIDEBAR_STYLES[idx]}>
                <div className="text-[11px] font-bold uppercase tracking-wider text-white/90 mb-1">{label}</div>
                <div className="flex items-start gap-2 justify-between">
                  <div className="flex items-start gap-2 min-w-0">
                    <Icon className="w-4 h-4 text-white shrink-0 mt-0.5" aria-hidden />
                    <span className="font-semibold leading-snug">{bodies[idx]}</span>
                  </div>
                  <ArrowDown className="w-4 h-4 sm:w-5 sm:h-5 text-white shrink-0 opacity-90" strokeWidth={2.5} aria-hidden />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

const TopBenefitStack = ({ t, text }) => (
  <Card className="rotating-border-soft p-6 md:p-7 rounded-2xl shadow-lg shadow-[#14A098]/20">
    <p className="text-sm font-semibold uppercase tracking-widest text-emerald-700">{t('itr.benefits_label', 'Benefits')}</p>
    <p className="text-2xl md:text-[2rem] leading-tight font-extrabold text-[#0f2540] mt-2">
      {text}
    </p>
  </Card>
);

const SourcePricingSection = ({ t, selectedSources, guide }) => {
  const selectedSourceSet = new Set(Array.isArray(selectedSources) ? selectedSources : []);
  return (
  <Card
    id="source-pricing-guide"
    className="scroll-mt-24 md:scroll-mt-28 p-6 md:p-8 rounded-2xl border border-[#14A098]/20 bg-white shadow-sm"
  >
    <div className="mb-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
      <h3 className="text-[1.75rem] leading-tight sm:text-2xl font-extrabold text-[#0f2540]">
        {t('itf.table_title_simple', 'Source → ITR type')}
      </h3>
      <Badge className="shrink-0 bg-[#14A098]/10 text-[#0f2540] border border-[#14A098]/20 whitespace-nowrap">
        {t('itf.table_badge', 'Easy client guide')}
      </Badge>
    </div>
    <p className="text-sm text-gray-600 mb-5">
      {t('itf.table_intro_simple', 'This table is made to avoid confusion. Choose your source of income and see the likely ITR type in one place.')}
    </p>
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <div>
        <table className="w-full table-fixed">
          <thead>
            <tr className="bg-gray-100/80 border-b border-gray-200">
              <th className="w-[35%] px-2.5 sm:px-4 py-2.5 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wide">
                {t('itf.col_source', 'Source')}
              </th>
              <th className="w-[65%] px-2.5 sm:px-4 py-2.5 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase tracking-wide">
                {t('itf.col_itr', 'Recommended ITR')}
              </th>
            </tr>
          </thead>
          <tbody>
            {guide.map((item, index) => (
              <tr
                key={item.source}
                className={`${selectedSourceSet.has(item.source) ? 'bg-teal-50/70' : 'bg-white'} ${index !== guide.length - 1 ? 'border-b border-gray-100' : ''}`}
              >
                <td className="px-2.5 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-[#0f2540] align-top break-words">{translateIncomeSource(t, item.source)}</td>
                <td className="px-2.5 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-[#0f2540] align-top break-words leading-snug">{translateItrGuideField(t, item, 'itr')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    <p className="text-xs text-gray-500 mt-4">
      {t('itf.table_note', 'Note: Final ITR form is confirmed by our tax expert after checking your full case.')}
    </p>
  </Card>
  );
};

const BULLET_KEYS = {
  ITR_NOTICE: ['itf.notice_1', 'itf.notice_2', 'itf.notice_3'],
  ADVANCE_TAX: ['itf.adv_1', 'itf.adv_2', 'itf.adv_3'],
  CAPITAL_GAINS: ['itf.cg_1', 'itf.cg_2', 'itf.cg_3'],
};

const BULLET_FALLBACKS = {
  'itf.notice_1': 'Notice understanding & reply drafting guidance',
  'itf.notice_2': 'Document checklist based on your notice type',
  'itf.notice_3': 'Clear next steps before any submission',
  'itf.adv_1': 'Income estimate and instalment planning',
  'itf.adv_2': 'Payment guidance aligned to deadlines',
  'itf.adv_3': 'Simple explanations without jargon',
  'itf.cg_1': 'Gain computation approach for your case',
  'itf.cg_2': 'Supporting schedules / summaries guidance',
  'itf.cg_3': 'Clear filing linkage based on your facts',
  'itf.gen_1': 'Requirements checklist for your selected service',
  'itf.gen_2': 'Pricing clarity before work begins',
  'itf.gen_3': 'End-to-end coordination with your assigned agent',
  'itf.bullet_itr_1': 'Help to choose correct ITR form (1 / 2 / 3 / 4)',
  'itf.bullet_itr_2': 'Final error check before submission',
  'itf.bullet_itr_3': 'Acknowledgement and support after filing',
};

const SelectedSourceGuide = ({ t, sources, serviceCode, guide }) => {
  const ordered = orderSources(Array.isArray(sources) ? sources : []);
  if (ordered.length === 0) return null;
  const rows = ordered.map((src) => guide.find((item) => item.source === src)).filter(Boolean);
  if (rows.length === 0) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold text-[#0f2540]">
          {t('itf.source_banner_multi', 'Based on your selected income source(s):')}{' '}
          <span className="text-[#14A098]">{ordered.map((src) => translateIncomeSource(t, src)).join(', ')}</span>
        </p>
        <a
          href="#source-pricing-guide"
          onClick={() =>
            trackEvent({
              event_name: 'cta_click',
              event_action: 'click',
              event_status: 'success',
              event_category: 'navigation',
              cta_name: 'itr_form_banner_view_full_pricing',
              page_path: '/file-itr',
              funnel_name: 'ITR_FULL_FORM',
              form_name: 'IncomeTaxForm',
              journey_id: getJourneyId('ITR_FULL_FORM'),
              service_code: serviceCode,
            })
          }
          className="text-xs font-semibold text-[#14A098] underline shrink-0"
        >
          {t('itf.view_full_table', 'View full ITR guide')}
        </a>
      </div>
      {rows.map((selected) => (
        <Card key={selected.source} className="p-4 rounded-xl border border-teal-200 bg-teal-50/70">
          <p className="text-xs font-semibold text-[#0f2540] mb-2">
            <span className="text-[#14A098]">{translateIncomeSource(t, selected.source)}</span>
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="rounded-lg bg-white border border-teal-100 p-3">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">{t('itf.col_itr', 'Recommended ITR')}</p>
              <p className="font-semibold text-[#0f2540]">{translateItrGuideField(t, selected, 'itr')}</p>
            </div>
            <div className="rounded-lg bg-white border border-teal-100 p-3">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">{t('itf.keep_ready', 'Keep ready')}</p>
              <p className="text-sm text-gray-700">{translateItrGuideField(t, selected, 'docs')}</p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

// ----- Main page -----
const IncomeTaxForm = () => {
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const serviceQuery = params.get('service');
  const serviceCode = useMemo(() => {
    const raw = serviceQuery?.trim();
    if (!raw) return 'ITR_FILING';
    const hit = SERVICE_CATALOG.find((s) => s.code === raw)
      || SERVICE_CATALOG.find((s) => s.code.toUpperCase() === raw.toUpperCase());
    return hit?.code ?? raw;
  }, [serviceQuery]);
  const serviceMeta = SERVICE_CATALOG.find((s) => s.code === serviceCode);
  const localizedServiceTitle = serviceMeta
    ? serviceTitle(serviceMeta.code, t, serviceMeta.title)
    : t('itf.default_service', 'Income tax service');
  const serviceCopy = useMemo(
    () => getIncomeTaxServiceCopy(serviceCode, localizedServiceTitle, t),
    [serviceCode, localizedServiceTitle, t],
  );

  const [form, setForm] = useState(() => ({
    ...initialState,
    language: defaultSpokenLanguageForUiLocale(locale),
  }));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const sourcePriceGuide = SOURCE_ITR_PRICING_GUIDE;

  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, []);

  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'ITR_FULL_FORM',
      form_name: 'IncomeTaxForm',
      service_code: serviceCode,
      open_event_name: 'itr_form_open',
    }),
    [serviceCode],
  );

  useEffect(() => {
    const def = defaultSpokenLanguageForUiLocale(locale);
    if (!def) return;
    setForm((prev) => (prev.language ? prev : { ...prev, language: def }));
  }, [locale]);

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (submitError) setSubmitError('');
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validateForm(form, t);
    setErrors(errs);
    setSubmitError('');
    if (Object.keys(errs).length > 0) {
      trackEvent({
        event_name: 'itr_form_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'IncomeTaxForm',
        funnel_name: 'ITR_FULL_FORM',
        funnel_step_number: 3,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('ITR_FULL_FORM'),
        service_code: serviceCode,
        error_code: 'validation_error',
        error_message: 'Client-side validation failed',
      });
      toast.error(t('form.err_fix_toast', 'Please fix the highlighted errors'));
      return;
    }
    trackEvent({
      event_name: 'itr_form_submit_attempt',
      event_action: 'submit',
      event_status: 'attempt',
      event_category: 'form',
      form_name: 'IncomeTaxForm',
      funnel_name: 'ITR_FULL_FORM',
      funnel_step_number: 3,
      funnel_step_name: 'submit',
      journey_id: getJourneyId('ITR_FULL_FORM'),
      service_code: serviceCode,
      phone_number: form.mobile,
    });
    setSubmitting(true);
    const capture = buildCampaignCapturePayload();
    const payload = {
      ...buildPayload(form, serviceCode),
      ...capture,
      lead_source: capture.lead_source || 'WEBSITE',
    };
    try {
      if (!isGoogleSheetsConfigured()) {
        trackEvent({
          event_name: 'itr_form_submit_failed',
          event_action: 'submit',
          event_status: 'failed',
          event_category: 'form',
          form_name: 'IncomeTaxForm',
          funnel_name: 'ITR_FULL_FORM',
          funnel_step_number: 3,
          funnel_step_name: 'submit',
          journey_id: getJourneyId('ITR_FULL_FORM'),
          service_code: serviceCode,
          phone_number: form.mobile,
          error_code: 'missing_sheets_env',
          error_message: 'REACT_APP_GOOGLE_SHEETS_WEBAPP_URL not set',
        });
        toast.error(
          t(
            'form.err_sheets_missing',
            'Add REACT_APP_GOOGLE_SHEETS_WEBAPP_URL in frontend/.env (Apps Script URL ending in /exec), then restart the dev server.',
          ),
        );
        return;
      }
      appendLeadToGoogleSheets({
        form: 'income_tax',
        summary: `${payload.client_name || '—'} | ${payload.mobile} | FY: ${payload.financial_year} | Income: ${payload.source_of_income} | ${serviceCode}`,
        detail: payload,
      });
      trackEvent({
        event_name: 'itr_form_submit_success',
        event_action: 'submit',
        event_status: 'success',
        event_category: 'form',
        form_name: 'IncomeTaxForm',
        funnel_name: 'ITR_FULL_FORM',
        funnel_step_number: 3,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('ITR_FULL_FORM', { reset: true }),
        service_code: serviceCode,
        phone_number: form.mobile,
        api_name: 'google_sheets',
        api_status_code: null,
      });
      toast.success(
        t('form.toast_submit_ok_public', 'Submitted successfully. Our team will contact you soon.'),
      );
      setSuccess(payload);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[SolveTax] submission failed', err);
      trackEvent({
        event_name: 'itr_form_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'IncomeTaxForm',
        funnel_name: 'ITR_FULL_FORM',
        funnel_step_number: 3,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('ITR_FULL_FORM'),
        service_code: serviceCode,
        phone_number: form.mobile,
        error_code: 'unexpected',
        error_message: String(err?.message || err),
      });
      toast.error(t('contact.toast_backend', 'Backend: {msg}', { msg: err?.message || 'Unexpected error' }));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return <SuccessView payload={success} onAbout={() => goToPostSubmit(navigate, success.client_name)} />;
  }

  return (
    <div className="pt-28 pb-16 hero-gradient min-h-screen">
      <div className="w-full max-w-[1680px] mx-auto px-2 sm:px-3 md:px-5 lg:px-6">
        {/* 2×2 on lg: benefit above sidebar (tight stack); mobile order = benefit → hero → sidebar → form */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-4 xl:gap-x-7 xl:gap-y-5 items-start">
          <div className="order-1 lg:order-none lg:col-span-4 xl:col-span-3 lg:row-start-1 w-full min-w-0">
            <TopBenefitStack t={t} text={serviceCopy.benefitLine} />
          </div>
          <div className="order-2 lg:order-none lg:col-span-8 xl:col-span-9 lg:col-start-5 xl:col-start-4 lg:row-start-1 text-center min-w-0 lg:pl-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 mb-4 shadow-sm">
            <Sparkles className="w-4 h-4 text-[#14A098]" />
              <span className="text-xs font-semibold text-[#0f2540]">{localizedServiceTitle}{t('itf.badge_ay', '• AY 2025-26')}</span>
            </div>
            <h1 className="ui-title-xl mb-3">{localizedServiceTitle}</h1>
            <p className="ui-body max-w-3xl mx-auto mb-4">
              {t('itr.main_before_hero', 'Fill this simple form in 2 minutes. Our tax expert will call you within 2 hours and ')}
              {serviceCopy.heroHelp}
            </p>
            <a
              href="#itr-form"
              onClick={() =>
                trackEvent({
                  event_name: 'cta_click',
                  event_action: 'click',
                  event_status: 'success',
                  event_category: 'navigation',
                  cta_name: 'itr_top_fill_form_cta',
                  page_path: '/file-itr',
                  funnel_name: 'ITR_FULL_FORM',
                  form_name: 'IncomeTaxForm',
                  journey_id: getJourneyId('ITR_FULL_FORM'),
                  service_code: serviceCode,
                })
              }
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#14A098] bg-[#14A098] px-6 h-11 text-sm font-semibold text-white shadow-md shadow-[#14A098]/35 transition-colors hover:bg-[#0e7a74] hover:border-[#0e7a74] active:bg-[#0c6b66] active:border-[#0c6b66] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14A098] focus-visible:ring-offset-2 whitespace-normal text-center leading-snug"
            >
              {serviceCopy.promoCta}
              <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
            </a>
          </div>
          <div className="order-3 lg:order-none lg:col-span-4 xl:col-span-3 lg:row-start-2 w-full min-w-0 lg:sticky lg:top-28 lg:self-start">
            <Sidebar t={t} step4Text={serviceCopy.step4} serviceCode={serviceCode} submitDetailsLabel={t('form.submit_details', 'Submit Details')} />
        </div>
          <div className="order-4 lg:order-none lg:col-span-8 xl:col-span-9 lg:col-start-5 xl:col-start-4 lg:row-start-2 min-w-0">
            <Card className="p-6 md:p-10 rounded-2xl border border-gray-100 shadow-xl bg-white">
              <form id="itr-form" onSubmit={handleSubmit} className="space-y-8">
              {submitError && (
                <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <p className="font-medium">{submitError}</p>
                  <p className="mt-1">
                    {t('form.need_help_case', 'Need help with this case?')}{' '}
                    <Link to="/contact" className="font-semibold underline hover:text-rose-800">
                      {t('form.contact_support', 'Contact support')}
                    </Link>
                  </p>
                </div>
              )}
              <FormSection title={t('itr.form_primary', 'Primary Details')} icon={User}>
                <div className="grid md:grid-cols-2 gap-5">
                  <FieldWrap htmlFor="itr-client_name" label={t('itr.full_name', 'Full Name')} icon={User} required error={errors.client_name}>
                    <Input id="itr-client_name" name="client_name" autoComplete="name" value={form.client_name} onChange={(e) => setField('client_name', e.target.value)} placeholder={t('itr.placeholder_name', 'e.g. Rahul Sharma')} maxLength={150} />
                  </FieldWrap>
                  <FieldWrap htmlFor="itr-mobile" label={t('itr.mobile', 'Mobile Number')} icon={Phone} required error={errors.mobile}>
                    <Input
                      id="itr-mobile"
                      name="mobile"
                      autoComplete="tel"
                      value={form.mobile}
                      onChange={(e) => setField('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder={t('itr.placeholder_mobile', '10-digit mobile')}
                      inputMode="numeric"
                      className={errors.mobile ? 'border-rose-500 focus-visible:ring-rose-500' : ''}
                    />
                  </FieldWrap>
                  <FieldWrap htmlFor="itr-language" label={t('itr.pref_lang', 'Preferred Language')} icon={Globe} required error={errors.language}>
                    <Select value={form.language} onValueChange={(v) => setField('language', v)}>
                      <SelectTrigger id="itr-language" name="language" aria-label={t('itr.pref_lang', 'Preferred Language')}><SelectValue placeholder={t('itr.placeholder_lang', 'Select language')} /></SelectTrigger>
                      <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </FieldWrap>
                  <FieldWrap htmlFor="itr-state" label={t('itr.state', 'State')} icon={MapPin} required error={errors.state}>
                    <Select value={form.state} onValueChange={(v) => setField('state', v)}>
                      <SelectTrigger id="itr-state" name="state" aria-label={t('itr.state', 'State')}><SelectValue placeholder={t('itr.placeholder_state', 'Select state')} /></SelectTrigger>
                      <SelectContent className="max-h-72">{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </FieldWrap>
                </div>
              </FormSection>

              <FormSection title={t('itr.form_tax', 'Tax Filing Details')} icon={FileText} hint={t('itr.form_pan_note', 'PAN and password are optional for now')}>
                <div className="grid md:grid-cols-2 gap-5">
                  <FieldWrap htmlFor="itr-financial_year" label={t('itr.fin_year', 'Financial Year')} icon={Calendar} required error={errors.financial_year} hint={t('itr.multi_select_hint', 'Select all that apply')}>
                    <MultiCheckboxGroup
                      groupId="itr-financial_year"
                      options={FINANCIAL_YEARS}
                      selected={form.financial_year}
                      error={errors.financial_year}
                      onToggle={(opt, checked) => {
                        const next = checked
                          ? orderYears([...new Set([...form.financial_year, opt])])
                          : form.financial_year.filter((y) => y !== opt);
                        setField('financial_year', next);
                      }}
                    />
                  </FieldWrap>
                  <FieldWrap htmlFor="itr-source_of_income" label={t('itr.source_income', 'Source of Income')} icon={Briefcase} required error={errors.source_of_income} hint={t('itr.multi_select_hint', 'Select all that apply')}>
                    <MultiCheckboxGroup
                      groupId="itr-source_of_income"
                      options={SOURCES_OF_INCOME}
                      selected={form.source_of_income}
                      error={errors.source_of_income}
                      getOptionLabel={(opt) => translateIncomeSource(t, opt)}
                      onToggle={(opt, checked) => {
                        const next = checked
                          ? orderSources([...new Set([...form.source_of_income, opt])])
                          : form.source_of_income.filter((s) => s !== opt);
                        setField('source_of_income', next);
                        trackEvent({
                          event_name: 'itr_source_selected',
                          event_action: 'change',
                          event_status: 'success',
                          event_category: 'form',
                          form_name: 'IncomeTaxForm',
                          funnel_name: 'ITR_FULL_FORM',
                          funnel_step_number: 2,
                          funnel_step_name: 'tax_filing_details',
                          journey_id: getJourneyId('ITR_FULL_FORM'),
                          service_code: serviceCode,
                          event_label: orderSources(next).join(', '),
                        });
                      }}
                    />
                  </FieldWrap>
                </div>
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowMoreDetails((prev) => {
                        const nextValue = !prev;
                        trackEvent({
                          event_name: 'itr_show_more_toggle',
                          event_action: 'toggle',
                          event_status: 'success',
                          event_category: 'interaction',
                          form_name: 'IncomeTaxForm',
                          funnel_name: 'ITR_FULL_FORM',
                          funnel_step_number: 2,
                          funnel_step_name: 'tax_filing_details',
                          journey_id: getJourneyId('ITR_FULL_FORM'),
                          service_code: serviceCode,
                          event_label: nextValue ? 'expanded' : 'collapsed',
                        });
                        return nextValue;
                      });
                    }}
                    className="rounded-full border-[#14A098] text-[#14A098] hover:bg-[#14A098] hover:text-white"
                  >
                    {showMoreDetails ? t('sf.hide_more', 'Hide additional details') : t('sf.add_more', 'Add more details (optional)')}
                  </Button>
                </div>
                <SelectedSourceGuide t={t} sources={form.source_of_income} serviceCode={serviceCode} guide={sourcePriceGuide} />
                {showMoreDetails && (
                  <div className="grid md:grid-cols-2 gap-5 pt-4">
                    <FieldWrap htmlFor="itr-email_id" label={t('form.email_label', 'Email Address')} icon={Mail} error={errors.email_id}>
                      <Input id="itr-email_id" name="email_id" autoComplete="email" type="email" value={form.email_id} onChange={(e) => setField('email_id', e.target.value)} placeholder={t('sf.placeholder_email', 'your@email.com')} />
                    </FieldWrap>
                    <FieldWrap htmlFor="itr-pan_number" label={<span>{t('form.pan_label', 'PAN Number')} <Badge variant="secondary" className="ml-2 text-[10px] bg-gray-100 text-gray-600">{t('form.optional', 'Optional')}</Badge></span>} icon={FileText} error={errors.pan_number} hint={t('form.pan_hint', 'Format: AAAAA9999A. Add later if not handy.')}>
                      <Input id="itr-pan_number" name="pan_number" autoComplete="off" value={form.pan_number} onChange={(e) => setField('pan_number', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))} placeholder="ABCDE1234F" className="uppercase tracking-wider" />
                    </FieldWrap>
                    <div className="md:col-span-2">
                      <FieldWrap htmlFor="itr-password" label={<span>{t('form.pwd_label', 'IT Portal Password')} <Badge variant="secondary" className="ml-2 text-[10px] bg-gray-100 text-gray-600">{t('form.optional', 'Optional')}</Badge></span>} icon={KeyRound} hint={t('form.pwd_hint', 'Encrypted & accessed only by your assigned agent.')}>
                    <div className="relative">
                          <Input id="itr-password" name="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => setField('password', e.target.value)} placeholder={t('form.pwd_ph', 'Add later if not handy')} autoComplete="new-password" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#14A098]" aria-label={showPassword ? t('form.hide_pw', 'Hide password') : t('form.show_pw', 'Show password')}>
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </FieldWrap>
                </div>
                  </div>
                )}
              </FormSection>

              {showMoreDetails && (
                <FormSection title={t('form.additional_remarks', 'Additional Remarks')} icon={MessageSquare}>
                  <Textarea id="itr-remarks" name="remarks" value={form.remarks} onChange={(e) => setField('remarks', e.target.value)} rows={4} placeholder={t('form.remarks_ph', 'Any specific information or concerns you\'d like us to know (optional)')} />
              </FormSection>
              )}

              <div className="flex flex-wrap gap-3 pt-2 items-center">
                <Button type="submit" size="lg" disabled={submitting} className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full px-8 h-12 min-w-0 sm:min-w-[200px] max-w-full shadow-lg shadow-[#14A098]/30 whitespace-normal text-center leading-snug">
                  {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('itr.submitting', 'Submitting...')}</>) : (<>{t('form.submit_details', 'Submit Details')} <ArrowRight className="w-4 h-4 ml-2" /></>)}
                </Button>
                <Button type="button" variant="outline" onClick={() => setForm({ ...initialState, language: defaultSpokenLanguageForUiLocale(locale) })} className="rounded-full h-12 border-gray-300">{t('itr.reset', 'Reset')}</Button>
                {serviceCode === 'ITR_FILING' && (
                  <a
                    href="#source-pricing-guide"
                    onClick={() =>
                      trackEvent({
                        event_name: 'cta_click',
                        event_action: 'click',
                        event_status: 'success',
                        event_category: 'navigation',
                        cta_name: 'itr_form_see_pricing_anchor',
                        page_path: '/file-itr',
                        funnel_name: 'ITR_FULL_FORM',
                        form_name: 'IncomeTaxForm',
                        journey_id: getJourneyId('ITR_FULL_FORM'),
                        service_code: serviceCode,
                      })
                    }
                  >
                    <Button
                      type="button"
                      className="rounded-full h-12 bg-[#14A098] border-2 border-[#14A098] text-white hover:bg-[#0e7a74] hover:border-[#0e7a74] active:bg-[#0c6b66] active:border-[#0c6b66] shadow-md shadow-[#14A098]/30"
                    >
                      {t('itr.pricing_link', 'See Pricing and ITR\'s')}
                      <ArrowRight className="h-4 w-4 ml-2 shrink-0 opacity-95" aria-hidden />
                    </Button>
                  </a>
                )}
                <FormLegalNotice />
              </div>
            </form>
          </Card>
          </div>
        </div>
        {serviceCode === 'ITR_FILING' && (
          <div className="mt-8">
            <SourcePricingSection t={t} selectedSources={form.source_of_income} guide={sourcePriceGuide} />
          </div>
        )}
        {SERVICE_CONTENT[serviceCode] && (
          <ServiceContent code={serviceCode} title={localizedServiceTitle} content={SERVICE_CONTENT[serviceCode]} />
        )}
      </div>
    </div>
  );
};

const SuccessView = ({ payload, onAbout }) => {
  const { t } = useLanguage();
  useEffect(() => {
    const timer = setTimeout(() => onAbout(), 3000);
    return () => clearTimeout(timer);
  }, [onAbout]);

  const firstName = payload.client_name.split(' ')[0];

  return (
    <div className="pt-28 pb-16 hero-gradient min-h-screen">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <Card className="p-8 md:p-12 rounded-2xl border border-gray-100 shadow-xl bg-white text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-lg mb-6">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] mb-3">{t('form.success_thanks', 'Thank you, {name}!', { name: firstName })}</h1>
          <p className="text-gray-600 mb-6">{t('form.success_itr_body', 'Your request has been received. Our expert will reach out at +91 {phone} within 2 hours.', { phone: payload.mobile })}</p>
          <p className="text-sm text-gray-500">{t('contact.success_redirect', 'Redirecting...')}</p>
        </Card>
      </div>
    </div>
  );
};

export default IncomeTaxForm;
